import React, { useCallback, useRef } from 'react'
import { useControlledOverlay } from '@fs/zion-ui'
import AddExistingPersonPanel from './AddExistingPersonPanel'
import PreviousRelationshipsDialog from './components/PreviousRelationshipsDialog'
import ChoosePrimaryPersonDialog from './components/ChoosePrimaryPersonDialog'
import { useAddExistingPersonFlow } from './useAddExistingPersonFlow'
import { ADD_EXISTING_PERSON_STEPS } from './utils/addExistingPersonConstants'

/**
 * Renders the Add Existing Person flow: the selection InfoSheet, then whichever of the two confirmation
 * steps apply.
 *
 * @param {Object} params
 * @param {Object} params.overlay - `useOverlay()` object controlling the selection InfoSheet
 * @param {string} params.entryPoint - One of
 *   {@link ./utils/addExistingPersonConstants}.ADD_EXISTING_PERSON_ENTRY_POINTS
 * @param {string} [params.focusPersonName] - Focus person's name; omitted when there is no focus person
 * @param {string} [params.focusPersonId] - The focus person, who is never a selectable row
 * @param {Array<string>} [params.pendingMemberIds] - The subset of `groupMemberIds` staged but not yet saved
 * @param {Array<string>} [params.groupMemberIds] - Person ids already in the group, from the caller's
 *   live form state rather than SLS, so a name added but not yet saved cannot be added twice
 * @param {(result: import('./useAddExistingPersonFlow').AddExistingPersonResult) => void} [params.onSave]
 *   Called with everything the patron agreed to.
 * @returns {React.ReactElement}
 */
const AddExistingPersonFlow = ({
  overlay,
  entryPoint,
  focusPersonName,
  focusPersonId,
  groupMemberIds = [],
  pendingMemberIds = [],
  onSave = () => {},
}) => {
  // Dismissing a confirmation returns the patron to the control that raised it.
  const confirmButtonRef = useRef(null)

  // The sheet is only dismissed once there is nothing left to ask.
  const handleComplete = useCallback(
    (result) => {
      onSave(result)
      overlay.close()
    },
    [onSave, overlay]
  )

  const {
    step,
    movingPersons,
    atRiskGroups,
    previousRelationshipsApplied,
    selectPersons,
    confirmRelationshipRemoval,
    confirmPrimaryPersons,
    goBackToPreviousRelationships,
    reset,
  } = useAddExistingPersonFlow({
    entryPoint,
    pendingMemberIds,
    onComplete: handleComplete,
  })

  // Each dialog is mounted only while its own step is current, and `useControlledOverlay` reports a
  // close only from the mounted dialog's X / Escape / click-away — never from the step moving on. So
  // a close request here is always a dismissal of the current step.
  const previousRelationshipsOverlay = useControlledOverlay({
    isOpen: step === ADD_EXISTING_PERSON_STEPS.PREVIOUS_RELATIONSHIPS,
    onCloseRequested: reset,
    focusBackRef: confirmButtonRef,
  })

  const primaryPersonOverlay = useControlledOverlay({
    isOpen: step === ADD_EXISTING_PERSON_STEPS.PRIMARY_PERSON,
    onCloseRequested: reset,
    focusBackRef: confirmButtonRef,
  })

  return (
    <>
      <AddExistingPersonPanel
        overlay={overlay}
        focusPersonName={focusPersonName}
        focusPersonId={focusPersonId}
        groupMemberIds={groupMemberIds}
        confirmButtonRef={confirmButtonRef}
        onConfirm={selectPersons}
        onCancel={reset}
      />
      {/* Cancelling rewinds to the selection step, whose ticks the still-mounted sheet has kept. */}
      {step === ADD_EXISTING_PERSON_STEPS.PREVIOUS_RELATIONSHIPS && (
        <PreviousRelationshipsDialog
          overlay={previousRelationshipsOverlay}
          atRiskGroups={atRiskGroups}
          entryPoint={entryPoint}
          focusPersonName={focusPersonName}
          onCancel={reset}
          onContinue={confirmRelationshipRemoval}
        />
      )}
      {step === ADD_EXISTING_PERSON_STEPS.PRIMARY_PERSON && (
        <ChoosePrimaryPersonDialog
          overlay={primaryPersonOverlay}
          movingPersons={movingPersons}
          showBack={previousRelationshipsApplied}
          onBack={goBackToPreviousRelationships}
          onCancel={reset}
          onConfirm={confirmPrimaryPersons}
        />
      )}
    </>
  )
}

export default AddExistingPersonFlow
